import React, { createContext, useContext, useState } from 'react'
import { useLocation } from 'wouter'
import { GUIDE_REGISTRY } from '@/lib/guide-registry'
import type { SetupStatusItemKey } from '@/types/setup-status'

interface GuideContextValue {
  /** ID de guía pendiente de activarse en la página destino. Null cuando no hay guía en espera. */
  pendingGuideId: string | null
  /**
   * Lee el registro por clave de SetupStatus, establece el pendingGuideId
   * y navega a la ruta del módulo. No-op si la clave no tiene entrada en el registro.
   */
  launchGuide: (key: SetupStatusItemKey) => void
  /** Limpia el pendingGuideId una vez que la página destino activó la guía. */
  clearPendingGuide: () => void
}

const GuideContext = createContext<GuideContextValue | null>(null)

export function GuideProvider({ children }: { children: React.ReactNode }) {
  const [pendingGuideId, setPendingGuideId] = useState<string | null>(null)
  const [, navigate] = useLocation()

  const launchGuide = (key: SetupStatusItemKey) => {
    const entry = GUIDE_REGISTRY[key]
    if (!entry) return
    // Entradas sin guideId navegan sin arrancar ningún tour (no hay guía que lanzar).
    if (entry.guideId) {
      setPendingGuideId(entry.guideId)
    }
    navigate(entry.route)
  }

  const clearPendingGuide = () => {
    setPendingGuideId(null)
  }

  return (
    <GuideContext.Provider value={{ pendingGuideId, launchGuide, clearPendingGuide }}>
      {children}
    </GuideContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useGuideContext() {
  const ctx = useContext(GuideContext)
  if (!ctx) throw new Error('useGuideContext must be used within GuideProvider')
  return ctx
}
